/* ============================================================================
   VILLAINS TCG — Game ticker (cst-ticker.js)
   Seamless infinite marquee. The SSR track holds one readable group + a duplicate.
   This clones the base group until the track is wide enough that a one-group shift
   never exposes empty space, then the CSS animates by exactly one group
   (translateX(-100% / copies)) — constant speed, no gap, no jump.

   • Pause-on-hover/focus is CSS (animation-play-state).
   • Honors prefers-reduced-motion (keeps a single static group).
   • Recomputes on resize and after web fonts load (label widths can shift).
   • Re-runs on shopify:section:load so the Theme Editor stays seamless after edits
     (a re-rendered section drops back to the 2-group SSR fallback otherwise).
   • Clones are aria-hidden; only the first group is exposed to assistive tech.

   Global listeners are bound ONCE at module scope and always query live nodes, so
   repeated editor re-renders never leak listeners. buildTicker() is idempotent:
   it reconciles the clone count to what the current viewport needs on every call.
   ========================================================================== */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const buildTicker = (ticker) => {
    const track = ticker.querySelector('.cst-ticker__track');
    const viewport = ticker.querySelector('.cst-ticker__viewport');
    if (!track || !viewport) return;
    const base = track.querySelector('.cst-ticker__group');
    if (!base) return;

    const groups = () => track.querySelectorAll('.cst-ticker__group');

    // Reduced motion: collapse to a single (readable) group; CSS drops the animation.
    if (REDUCED.matches) {
      groups().forEach((g, i) => { if (i > 0) g.remove(); });
      track.style.removeProperty('--cst-ticker-copies');
      return;
    }

    const groupW = base.getBoundingClientRect().width;
    const vpW = viewport.getBoundingClientRect().width;
    if (groupW < 1 || vpW < 1) return;

    // At the loop point the track has shifted left by one group, so the remaining
    // (copies - 1) groups must still cover the viewport → copies ≥ vpW/groupW + 1.
    const copies = Math.max(2, Math.ceil(vpW / groupW) + 1);

    // Grow: append aria-hidden clones of the base group.
    let list = groups();
    for (let i = list.length; i < copies; i++) {
      const clone = base.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }
    // Shrink: trim extras (e.g. after a wide → narrow resize).
    list = groups();
    for (let i = list.length - 1; i >= copies; i--) list[i].remove();

    // The CSS keyframe ends at translateX(-100% / copies) = exactly one group.
    track.style.setProperty('--cst-ticker-copies', String(copies));
  };

  const buildAll = () => document.querySelectorAll('.cst-ticker').forEach(buildTicker);

  // Bind once — these always re-query the live DOM, so nothing leaks across
  // Theme-Editor section re-renders (old nodes are simply no longer matched).
  let raf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(buildAll);
  }, { passive: true });
  if (REDUCED.addEventListener) REDUCED.addEventListener('change', buildAll);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(buildAll);
  // Theme Editor replaces the section's innerHTML on edit and fires this event; the
  // re-injected <script> does NOT re-execute, so we rebuild the fresh SSR markup here.
  document.addEventListener('shopify:section:load', buildAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', buildAll);
  else buildAll();
})();
